import React, { useState } from 'react';
import { DOCUMENT_CATEGORY, DOCUMENT_VISIBILITY, options, today } from '../../constants/garmentCodes';
import { uploadFileToMinio } from '../../customHooks/S3Upload';

const inputCls = 'w-full text-sm border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-blue-400';
const LINKED_TYPES = ['Order', 'Requirement', 'Design', 'FabricOption', 'PaymentReceipt', 'LedgerEntry'];
const EMPTY = { engagementId: '', name: '', fileUrl: '', category: '', visibility: '1', linkedItemType: '', linkedItemId: '' };

const fileTypeFromName = (name = '') => {
  const ext = name.split('?')[0].split('.').pop();
  return ext && ext !== name ? ext.toUpperCase() : null;
};

export default function DocumentForm({ engagements, userId, onSubmit, onCancel }) {
  const [form, setForm] = useState(EMPTY);
  const [uploading, setUploading] = useState(false);
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const handleFileUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const url = await uploadFileToMinio(file, 'documents');
      if (url) {
        setForm((f) => ({
          ...f,
          fileUrl: url,
          name: f.name ? f.name : file.name,
        }));
      }
    } catch (err) {
      console.error('File upload failed:', err);
    } finally {
      setUploading(false);
    }
  };

  const submit = (e) => {
    e.preventDefault();
    onSubmit({
      engagementId: Number(form.engagementId),
      name: form.name.trim(),
      fileUrl: form.fileUrl.trim(),
      fileType: fileTypeFromName(form.name) || fileTypeFromName(form.fileUrl),
      category: form.category ? Number(form.category) : null,
      visibility: Number(form.visibility),
      linkedItemType: form.linkedItemType || null,
      linkedItemId: form.linkedItemId ? Number(form.linkedItemId) : null,
      uploadedByUserId: userId,
      uploadedDate: today(),
    });
    setForm(EMPTY);
  };

  return (
    <form onSubmit={submit} className="space-y-4">
      <div>
        <label className="block text-[11px] font-medium text-gray-600 mb-1">Engagement *</label>
        <select required value={form.engagementId} onChange={set('engagementId')} className={inputCls}>
          <option value="">Select engagement</option>
          {engagements.map((e) => <option key={e.id} value={e.id}>{e.name}</option>)}
        </select>
      </div>

      <div>
        <label className="block text-[11px] font-medium text-gray-600 mb-1">Upload File (Minio)</label>
        <div className="flex items-center gap-3">
          <input
            type="file"
            onChange={handleFileUpload}
            disabled={uploading}
            className="text-xs text-gray-600 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:text-xs file:font-medium file:bg-blue-50 file:text-[#1b3a6b] hover:file:bg-blue-100"
          />
          {uploading && <span className="text-[11px] text-[#1b3a6b] font-medium">Uploading to Minio...</span>}
        </div>
      </div>

      <div>
        <label className="block text-[11px] font-medium text-gray-600 mb-1">Document Name *</label>
        <input required value={form.name} onChange={set('name')} placeholder="e.g. TechPack_V2.pdf" className={inputCls} />
      </div>
      <div>
        <label className="block text-[11px] font-medium text-gray-600 mb-1">File URL *</label>
        <input required type="url" value={form.fileUrl} onChange={set('fileUrl')} placeholder="https://…" className={inputCls} />
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Category</label>
          <select value={form.category} onChange={set('category')} className={inputCls}>
            <option value="">Select category</option>
            {options(DOCUMENT_CATEGORY).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Visibility</label>
          <select value={form.visibility} onChange={set('visibility')} className={inputCls}>
            {options(DOCUMENT_VISIBILITY).map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Linked Item Type</label>
          <select value={form.linkedItemType} onChange={set('linkedItemType')} className={inputCls}>
            <option value="">None</option>
            {LINKED_TYPES.map((t) => <option key={t}>{t}</option>)}
          </select>
        </div>
        <div>
          <label className="block text-[11px] font-medium text-gray-600 mb-1">Linked Item ID</label>
          <input type="number" min="1" value={form.linkedItemId} onChange={set('linkedItemId')} disabled={!form.linkedItemType} className={`${inputCls} font-mono`} />
        </div>
      </div>
      <div className="flex gap-3 pt-2">
        <button type="button" onClick={onCancel} className="flex-1 px-4 py-2 text-sm text-gray-600 border border-gray-200 rounded-lg hover:bg-gray-50">Cancel</button>
        <button type="submit" disabled={uploading} className="flex-1 px-4 py-2 text-sm font-medium text-white bg-[#1b3a6b] rounded-lg hover:bg-[#152d55] disabled:opacity-50">Add Document</button>
      </div>
    </form>
  );
}
